/**
 * Builder-specific modifier utilities
 * These are separate from the existing modifierUtils.ts and provide
 * specialized logic for the ModifierBuilderModal component
 */

export interface BuilderModifier {
  name: string;
  is_forced?: boolean | string;
  is_multi_select?: string;
  max_options_to_select?: string | number;
  options: any[];
}

export interface BuilderSelection {
  name: string;
  options: any[];
}

/**
 * Check if a modifier is required
 * Required means the user must make a selection to proceed
 */
export const isBuilderModifierRequired = (modifier: BuilderModifier): boolean => {
  return modifier.is_forced === true || 
         modifier.is_forced === "yes" ||
         modifier.is_forced?.toString().toLowerCase() === "yes";
};

/**
 * Check if a modifier allows multiple selections (checkbox)
 * Returns false for single select (radio button)
 */
export const isBuilderModifierMultiSelect = (modifier: BuilderModifier): boolean => {
  return modifier.is_multi_select?.toLowerCase() === "yes";
};

/**
 * Get the maximum number of selections allowed
 * Returns null for unlimited selections
 */
export const getBuilderMaxSelections = (modifier: BuilderModifier): number | null => {
  if (!isBuilderModifierMultiSelect(modifier)) return 1;
  
  const max = modifier.max_options_to_select;
  
  // Empty string, null, or undefined = unlimited
  if (max === "" || max === null || max === undefined) {
    return null;
  }
  
  const numMax = Number(max);
  return !isNaN(numMax) && numMax > 0 ? numMax : null;
};

/**
 * Check if a modifier section is complete
 * Complete means it meets all requirements (or is optional and can be skipped)
 */
export const isBuilderSectionComplete = (
  modifier: BuilderModifier,
  selections: BuilderSelection[]
): boolean => {
  const selected = selections.find(s => s.name === modifier.name);
  const selectionCount = selected?.options.length || 0;
  
  // If not required, always complete
  if (!isBuilderModifierRequired(modifier)) {
    return true;
  }
  
  // Required but no selections = incomplete
  if (selectionCount === 0) return false;
  
  const maxSelections = getBuilderMaxSelections(modifier);
  
  // If max is null (unlimited), need at least 1
  if (maxSelections === null) {
    return selectionCount >= 1;
  }
  
  // If max has a value, need exactly that many
  return selectionCount === maxSelections;
};

/**
 * Check if a modifier section should be enabled (progressive unlock)
 * First section is always enabled
 * Subsequent sections enabled only if previous section is complete
 */
export const isBuilderSectionEnabled = (
  index: number,
  modifiers: BuilderModifier[],
  selections: BuilderSelection[]
): boolean => {
  // First section always enabled
  if (index === 0) return true;
  
  // Check if previous section is complete
  const previousModifier = modifiers[index - 1];
  return isBuilderSectionComplete(previousModifier, selections);
};

/**
 * Check if the form is valid for submission (Add to Cart)
 * Valid means all required sections are complete
 */
export const canBuilderAddToCart = (
  modifiers: BuilderModifier[],
  selections: BuilderSelection[]
): boolean => {
  // Check all required sections are complete
  for (const modifier of modifiers) {
    if (isBuilderModifierRequired(modifier)) {
      if (!isBuilderSectionComplete(modifier, selections)) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Handle option selection with proper multi-select logic
 * For multi-select: toggle option on/off
 * For single-select: replace existing selection
 */
export const handleBuilderOptionSelection = (
  modifierName: string,
  option: any,
  selections: BuilderSelection[],
  modifier: BuilderModifier
): BuilderSelection[] => {
  const existingIndex = selections.findIndex(s => s.name === modifierName);
  const isMulti = isBuilderModifierMultiSelect(modifier);
  
  if (existingIndex >= 0) {
    const existing = selections[existingIndex];
    
    if (isMulti) {
      // Multi-select: toggle option
      const optionIndex = existing.options.findIndex(o => o.name === option.name);
      
      if (optionIndex >= 0) {
        // Remove option
        const newOptions = existing.options.filter((_, i) => i !== optionIndex);
        if (newOptions.length === 0) {
          // Remove entire selection if empty
          return selections.filter((_, i) => i !== existingIndex);
        } else {
          // Update with remaining options
          const newSelections = [...selections];
          newSelections[existingIndex] = { ...existing, options: newOptions };
          return newSelections;
        }
      } else {
        // Add option
        const newSelections = [...selections];
        newSelections[existingIndex] = {
          ...existing,
          options: [...existing.options, option]
        };
        return newSelections;
      }
    } else {
      // Single select: replace
      const newSelections = [...selections];
      newSelections[existingIndex] = { name: modifierName, options: [option] };
      return newSelections;
    }
  } else {
    // Add new selection
    return [...selections, { name: modifierName, options: [option] }];
  }
};

/**
 * Check if an option is currently selected
 */
export const isBuilderOptionSelected = (
  modifierName: string,
  optionName: string,
  selections: BuilderSelection[]
): boolean => {
  const modifier = selections.find(s => s.name === modifierName);
  return modifier ? modifier.options.some(o => o.name === optionName) : false;
};

/**
 * Get the count of selected options for a modifier
 */
export const getBuilderSelectionCount = (
  modifierName: string,
  selections: BuilderSelection[]
): number => {
  const modifier = selections.find(s => s.name === modifierName);
  return modifier?.options.length || 0;
};

/**
 * Check if max selections reached for a modifier
 * Used to disable remaining options
 */
export const isBuilderMaxReached = (
  modifier: BuilderModifier,
  selections: BuilderSelection[]
): boolean => {
  const maxSelections = getBuilderMaxSelections(modifier);
  
  // If unlimited, never reached
  if (maxSelections === null) return false;
  
  const currentCount = getBuilderSelectionCount(modifier.name, selections);
  return currentCount >= maxSelections;
};
